import * as SelectPrimitive from "@radix-ui/react-select";
import type { ReactNode } from "react";
import { useId } from "react";

import { cn } from "../lib/cn";
import { FOCUS_RING, REDUCED_MOTION_SAFE } from "../lib/focus";
import { omitUndefined } from "../lib/optional";
import { NEUTRAL_SURFACE_FRAME } from "../lib/surface";

export type typeSelectItem = {
  value: string;
  label: string;
  disabled?: boolean | undefined;
};

export type typeSelectProps = {
  /** Sits above the control. Never replaced by the placeholder. */
  label: string;
  items: readonly typeSelectItem[];
  /**
   * Controlled value. On a route screen this comes from a query param, not
   * component state — the URL is the source of truth so a
   * refresh mid-flow lands back on the same selection.
   */
  value?: string | undefined;
  defaultValue?: string | undefined;
  onValueChange?: ((value: string) => void) | undefined;
  /** Shown when nothing is selected. Not a substitute for `label`. */
  placeholder?: string | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  labelHidden?: boolean | undefined;
  name?: string | undefined;
  className?: string | undefined;
};

const ChevronIcon = () => (
  <svg
    className="h-icon-md w-icon-md shrink-0 text-ink-secondary"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m5 8 5 5 5-5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    className="h-icon-sm w-icon-sm"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m3 8.5 3.5 3.5L13 5"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * A labeled single-choice select, on Radix so the listbox keyboard contract
 * (type-ahead, Home/End, Escape, roving focus) is the platform's and not ours.
 *
 * A native `<select>` would be smaller code and a worse control: it cannot show
 * a check on the active row, cannot be styled consistently across iOS and
 * Android, and gives no hook for the disabled-option treatment a locked round
 * needs.
 */
export const Select = ({
  label,
  items,
  value,
  defaultValue,
  onValueChange,
  placeholder = "Choose one",
  hint,
  error,
  required = false,
  disabled = false,
  labelHidden = false,
  name,
  className,
}: typeSelectProps): ReactNode => {
  const generatedId = useId();
  const triggerId = `${generatedId}-trigger`;
  const labelId = `${generatedId}-label`;
  const hintId = `${generatedId}-hint`;
  const errorId = `${generatedId}-error`;
  const invalid = typeof error === "string" && error.length > 0;

  return (
    <div className="flex w-full flex-col gap-1">
      <span
        id={labelId}
        className={cn("text-caption text-ink", labelHidden && "sr-only")}
      >
        {label}
        {required ? (
          <span className="text-error" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
      </span>

      <SelectPrimitive.Root
        disabled={disabled}
        required={required}
        {...omitUndefined({ value, defaultValue, onValueChange, name })}
      >
        <SelectPrimitive.Trigger
          id={triggerId}
          aria-labelledby={labelId}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? errorId : hint ? hintId : undefined}
          className={cn(
            "flex h-control-height w-full items-center justify-between gap-2 rounded-sm border bg-surface px-3",
            "text-input text-ink",
            "transition-colors duration-100 ease-standard",
            "data-[placeholder]:text-ink-tertiary",
            REDUCED_MOTION_SAFE,
            FOCUS_RING,
            "disabled:cursor-not-allowed disabled:border-disabled-border disabled:bg-disabled-bg disabled:text-disabled-text",
            invalid ? "border-error" : "border-line hover:border-line-strong",
            className,
          )}
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon>
            <ChevronIcon />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={4}
            data-testid="select-popover"
            className={cn(
              // z-index comes from the token, not a hand-picked number — the
              // error modal has to be able to outrank every one of these.
              "z-[var(--sm-z-dropdown)] max-h-[min(20rem,var(--radix-select-content-available-height,20rem))] min-w-[var(--radix-select-trigger-width)] overflow-hidden",
              // The prior hairline frame token measured 1.26 light and 1.10
              // dark here and failed CONTRAST_FLOOR.nonText (ISSUE-633).
              "rounded-md bg-surface-elevated shadow-md",
              NEUTRAL_SURFACE_FRAME,
              // Radix drives data-state; the theme owns the curve and duration.
              "data-[state=open]:animate-popover-in data-[state=closed]:animate-popover-out",
              REDUCED_MOTION_SAFE,
            )}
          >
            <SelectPrimitive.Viewport className="p-1">
              {items.map((item) => (
                <SelectPrimitive.Item
                  key={item.value}
                  value={item.value}
                  disabled={item.disabled ?? false}
                  className={cn(
                    // 44px minimum, because this list is tapped with a thumb.
                    "relative flex min-h-touch-target cursor-pointer select-none items-center gap-2",
                    "rounded-sm py-2 pr-2 pl-8 text-body text-ink outline-none",
                    "data-[highlighted]:bg-primary-tint data-[highlighted]:text-primary",
                    "data-[state=checked]:font-bold",
                    "data-[disabled]:pointer-events-none data-[disabled]:text-disabled-text",
                  )}
                >
                  <SelectPrimitive.ItemIndicator className="absolute left-2 flex items-center text-primary">
                    <CheckIcon />
                  </SelectPrimitive.ItemIndicator>
                  <SelectPrimitive.ItemText>
                    {item.label}
                  </SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>

      {invalid ? (
        <p id={errorId} role="alert" className="text-caption text-error">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-caption text-ink-secondary">
          {hint}
        </p>
      ) : null}
    </div>
  );
};
